import { TRAVEL_VACCINES_SLUG, VACCINES_SLUG } from "@/consts/vaccination-slugs"
import { fetchDataWithPagination } from "@/lib/fetchDataWithPagination"
import { getStoryblokApi } from "@/lib/storyblok"
import { mergeVaccineCampaign } from "@/storyblok/vaccinationRelations"
import {
  ArticleTagStory,
  ClinicFilterProps,
  ClinicItemType,
  FactAdviceListItem,
  FilterTagStory,
  RegionsItem,
  VaccinationLocation
} from "@/types/NonSyncedTypes"
import {
  ServiceTypeStoryblok,
  VaccinationBusLocationPageStoryblok,
  VaccinationClinicOpeningHoursStoryblok,
  VaccineCampaignStoryblok,
  VaccineTypeStoryblok
} from "@/types/component-types"
import { sortObjectsAlphabetically } from "@/utils/utils"
import { cacheVersion, defaultSbParams } from "@lib/defaultSbParams"
import { ISbStories, ISbStoriesParams, ISbStory, ISbStoryData } from "@storyblok/react/rsc"
import { addDays, format } from "date-fns"
import { cache } from "react"

// Cached per server request so repeated fetches of the same story hit Storyblok only once
// (e.g. generateMetadata + page for the story, or the clinic layout + page for the config).
// React cache() keys on the arguments, so version/release are part of the cache key.
const fetchStory = cache(
  async (
    slug: string,
    version: "draft" | "published",
    fromRelease: string | undefined,
    resolveRelations: string | undefined
  ) => {
    const sbParams: ISbStoriesParams = {
      version,
      from_release: fromRelease,
      resolve_links: "url",
      // Space cv for published reads (changes on publish), a time bucket for drafts. Without it
      // Storyblok's edge can serve content from before the last publish.
      cv: cacheVersion(version)
    }

    if (resolveRelations) {
      sbParams.resolve_relations = resolveRelations
    }

    const { data } = (await getStoryblokApi().get(`cdn/stories/${slug}`, sbParams)) as ISbStory

    return data?.story ?? null
  }
)

const getStory = async <T>(slug: string, options?: { resolveRelations?: string }) => {
  try {
    const isProduction = process.env.DOKTOR_ENVIRONMENT === "production"

    const story = await fetchStory(
      slug,
      isProduction ? "published" : "draft",
      isProduction ? process.env.STORYBLOK_CONTENT_PIPELINE : undefined,
      options?.resolveRelations
    )

    return {
      story: story as ISbStoryData<T>
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.status === 404) {
      return {
        story: null
      }
    } else {
      throw err
    }
  }
}

const getClinicTags = async (starts_with: string | undefined) => {
  const sbParams: ISbStoriesParams = {
    ...defaultSbParams,
    resolve_relations: "category_story",
    starts_with: starts_with,
    per_page: 100
  }

  const { data: tags } = (await getStoryblokApi().get(`cdn/stories`, sbParams)) as ISbStories

  const clinicTags = tags.stories.map(tag => {
    const tagName = tag.content.category_nicename ?? tag.content.region_nicename ?? tag.name
    return {
      id: tag.uuid,
      slug: tag.slug,
      name: tagName,
      order: tag.content.order,
      icon: tag.content.category_icon || undefined,
      tagStory: tag.content.category_story ? tags.rels.find(rel => rel.uuid === tag.content.category_story) : undefined,
      displayCollabodocLink: tag.content.display_collabodoc_link
    }
  }) as FilterTagStory[]

  return {
    tags: clinicTags
  }
}

const getClinicRegionTags = async (starts_with: string | undefined) => {
  const sbParams: ISbStoriesParams = {
    ...defaultSbParams,
    starts_with: starts_with,
    per_page: 100
  }

  const { data: tags } = (await getStoryblokApi().get(`cdn/stories`, sbParams)) as ISbStories

  const clinicTags = tags.stories.map(tag => {
    const tagName = tag.content.category_nicename ?? tag.content.region_nicename ?? tag.name

    return {
      id: tag.uuid,
      slug: tag.slug,
      name: tagName,
      order: tag.content.order,
      icon: tag.content.category_icon || undefined,
      displayCollabodocLink: tag.content.display_collabodoc_link
    }
  }) as FilterTagStory[]

  return {
    tags: clinicTags
  }
}

const getClinics = async (filters: ClinicFilterProps, regionParam = "", serviceParam = "") => {
  const sbParams: ISbStoriesParams = {
    ...defaultSbParams,
    by_slugs: "vardcentraler/*/config",
    per_page: 100,
    filter_query: {
      region_tags_story: {
        any_in_array: regionParam ? regionParam : null
      },
      clinic_tags_story: {
        any_in_array: serviceParam ? serviceParam : null
      }
    }
  }

  const { data } = (await getStoryblokApi().get(`cdn/stories`, sbParams)) as ISbStories
  const { tags: clinicTags } = await getClinicTags("data/clinictags")
  const { tags: regionTags } = await getClinicRegionTags("data/regioner")

  const returnedClinicTags: FilterTagStory[] = []
  const returnedRegionTags: FilterTagStory[] = []

  const clinics = data.stories.map(clinic => {
    const storyClinicTags: FilterTagStory[] = []
    const storyRegionTags: FilterTagStory[] = []

    clinic.content.clinic_tags_story.map((tag: string) => {
      const matchedTag = clinicTags.find(item => item.id === tag)
      if (matchedTag) {
        storyClinicTags.push(matchedTag)
        if (!returnedClinicTags.includes(matchedTag)) returnedClinicTags.push(matchedTag)
      }
    })

    clinic.content.region_tags_story.map((tag: string) => {
      const matchedTag = regionTags.find(item => item.id === tag)
      if (matchedTag) {
        storyRegionTags.push(matchedTag)
        if (!returnedRegionTags.includes(matchedTag)) returnedRegionTags.push(matchedTag)
      }
    })

    // Filter out not matching clinics by region
    if (filters.regionTag !== null) {
      const matchedRegionStoryTag = storyRegionTags.find(item => item.slug === filters.regionTag)
      if (!matchedRegionStoryTag) return undefined
    }

    // Filter out not matching clinics by clinic tag
    if (filters.clinicTag !== null && filters.clinicTag !== "") {
      const matchedClinicStoryTag = storyClinicTags.find(
        item => item.slug === filters.clinicTag || item.id === filters.clinicTag
      )
      if (!matchedClinicStoryTag) return undefined
    }

    return {
      id: clinic.id,
      clinic_name: clinic.content.clinic_name,
      slug: clinic.content.link.cached_url,
      clinic_tags: storyClinicTags,
      region_tags: storyRegionTags,
      address: clinic.content.address,
      phone_number: clinic.content.phone_number,
      latitude: parseFloat(clinic.content.latitude),
      longitude: parseFloat(clinic.content.longitude),
      location_url: clinic.content.location_url.cached_url,
      clinic_api_id: clinic.content.clinic_api_id || null,
      list_me_url: clinic.content?.list_me_link?.cached_url || "",
      clinic_hsa_id: clinic.content.clinic_hsa_id,
      clinic_region_id: clinic.content.clinic_region_id,
      collabodoc_url: clinic.content.collabodoc_url || "",
      clinic_description_button: clinic.content.clinic_description_button?.[0] ?? undefined,
      display_clinic_phone_number: clinic.content.display_clinic_phone_number,
      list_button: clinic.content.list_button?.[0] ?? undefined,
      list_button_emergency_aid_override: clinic.content.list_button_emergency_aid_override?.[0] ?? undefined,
      list_button_child_care_override: clinic.content.list_button_child_care_override?.[0] ?? undefined,
      list_button_cancer_prevention_override: clinic.content.list_button_cancer_prevention_override?.[0] ?? undefined
    }
  }) as ClinicItemType[]

  const returnedClinics = clinics.filter(clinic => clinic !== undefined)

  // Filter out clinic tags and region tags with 0 clinics
  const filteredClinicTags = returnedClinicTags.filter(tag =>
    returnedClinics.some(clinic => clinic.clinic_tags.includes(tag))
  )
  const filteredRegionTags = returnedRegionTags.filter(tag =>
    returnedClinics.some(clinic => clinic.region_tags.includes(tag))
  )

  return {
    clinics: sortObjectsAlphabetically(returnedClinics, "clinic_name"),
    clinicTags: sortObjectsAlphabetically(filteredClinicTags, "order"),
    regionTags: sortObjectsAlphabetically(filteredRegionTags, "name")
  }
}

const getRegionTags = async (starts_with: string | undefined) => {
  const sbParams: ISbStoriesParams = {
    ...defaultSbParams,
    starts_with: starts_with,
    per_page: 100
  }

  const { data: tags } = (await getStoryblokApi().get(`cdn/stories`, sbParams)) as ISbStories

  const clinicTags = tags.stories.map(tag => {
    const tagName = tag.content.category_nicename ?? tag.content.region_nicename ?? tag.name

    return {
      id: tag.uuid,
      slug: tag.slug,
      name: tagName,
      icon: tag.content.category_icon || undefined
    }
  }) as FilterTagStory[]

  return {
    tags: clinicTags
  }
}

const getVaccinationLocations = async (filter = "") => {
  try {
    const slug = "vaccinationsplats/"

    const customParams: ISbStoriesParams = {
      ...defaultSbParams,
      starts_with: slug,
      excluding_slugs: slug,
      is_startpage: false,
      per_page: 50,
      filter_query: {
        region: {
          in: filter ? filter : null
        }
      }
    }

    const { tags: regionTags } = await getClinicTags("data/regioner")
    const locations = await fetchDataWithPagination<ISbStoryData<VaccinationBusLocationPageStoryblok>[]>({
      customParams
    })

    const returnedRegionTags: FilterTagStory[] = []

    const result = locations.map(story => {
      if (!story.content) return null
      const storyRegionTags: FilterTagStory[] = []

      const matchedTag = regionTags.find(item => item.id === story.content.region)
      if (matchedTag) {
        storyRegionTags.push(matchedTag)
        if (!returnedRegionTags.includes(matchedTag)) returnedRegionTags.push(matchedTag)
      }

      return {
        uuid: story.id.toString(),
        id: story.id.toString(),
        name: story.name,
        url: story.full_slug,
        region: storyRegionTags,
        locations: story.content.locations
          ? story.content.locations
              .filter(location => location.latitude && location.longitude)
              .map(location => {
                if (location.latitude && location.longitude) {
                  return {
                    id: story.id,
                    name: story.name,
                    url: story.full_slug,
                    address: location.title,
                    center: {
                      lat: parseFloat(location.latitude),
                      lng: parseFloat(location.longitude)
                    }
                  }
                }
              })
              .filter(location => location !== null)
          : []
      }
    }) as VaccinationLocation[]

    return {
      locations: sortObjectsAlphabetically(result, "name"),
      regionTags: sortObjectsAlphabetically(regionTags, "name")
    }
  } catch (err) {
    console.error("Failed fetching: ", err)
    throw err
  }
}

const getArticleTags = async () => {
  const sbParams: ISbStoriesParams = {
    ...defaultSbParams,
    starts_with: "data/categories",
    per_page: 100
  }

  const { data: tags } = (await getStoryblokApi().get(`cdn/stories`, sbParams)) as ISbStories

  const articleTags = tags.stories.map(tag => {
    const tagName = tag.content.category_nicename ?? tag.content.region_nicename ?? tag.name

    return {
      id: tag.uuid,
      slug: tag.slug,
      name: tagName
    }
  }) as ArticleTagStory[]

  return {
    tags: sortObjectsAlphabetically(articleTags, "name")
  }
}

const getAuthorsById = async (ids: string[]) => {
  const customParams: ISbStoriesParams = {
    ...defaultSbParams,
    by_uuids: ids.join(",")
  }

  const { data: authors } = (await getStoryblokApi().get(`cdn/stories`, customParams)) as ISbStories

  return {
    authors: authors?.stories as ISbStoryData[]
  }
}

const getArticles = async (slug: string | undefined, page?: string, perPage?: number, filter = "", author = "") => {
  const articlesPerPage = perPage ? perPage : 6
  const pageNumber = page ? parseInt(page) : 1
  const tomorrowDate = addDays(new Date(), 1)
  const tomorrow = format(tomorrowDate, "yyyy-MM-dd")

  const customParams: ISbStoriesParams = {
    ...defaultSbParams,
    starts_with: slug,
    excluding_slugs: slug,
    sort_by: "content.published_date:desc",
    is_startpage: false,
    per_page: articlesPerPage,
    page: pageNumber,
    filter_query: {
      article_tags: {
        any_in_array: filter ? filter : null
      },
      author: {
        like: author ? author : null
      },
      published_date: {
        lt_date: tomorrow
      }
    }
  }

  const { data: articles, total } = (await getStoryblokApi().get(`cdn/stories`, customParams)) as ISbStories
  const totalPages = Math.ceil(total / articlesPerPage)

  return {
    articles: articles?.stories as ISbStoryData[],
    currentPage: pageNumber,
    totalPages: totalPages
  }
}

const searchFactAdvice = async (phrase: string, abortController: AbortController) => {
  try {
    const slug = "fakta-rad/"

    const { data, total } = await getStoryblokApi().get(
      `cdn/stories`,
      {
        ...defaultSbParams,
        search_term: phrase,
        starts_with: slug,
        excluding_slugs: slug,
        page: 1,
        per_page: 10
      },
      { signal: abortController.signal }
    )
    return {
      stories: (data.stories as ISbStoryData[]).map(story => ({
        id: story.id,
        name: story.name,
        url: story.full_slug,
        start_page: story.is_startpage,
        parent_id: story.parent_id,
        header_icon: story.content.header_icon ?? null
      })) as FactAdviceListItem[],
      total
    }
  } catch (err) {
    console.error("Failed fetching: ", err)
    throw err
  }
}

const getFactAdvice = async () => {
  try {
    const slug = "fakta-rad/"

    const customParams: ISbStoriesParams = {
      ...defaultSbParams,
      starts_with: slug,
      is_startpage: true,
      excluding_slugs: slug,
      per_page: 50,
      excluding_fields: "content"
    }

    let result = await fetchDataWithPagination({ customParams })

    // Sort top-level items by name
    result = sortObjectsAlphabetically(result, "name")

    const fetchFactAdvicePages = (await Promise.all(
      result.map(async story => {
        const customParams: ISbStoriesParams = {
          ...defaultSbParams,
          starts_with: story.full_slug,
          is_startpage: false,
          excluding_slugs: slug,
          per_page: 50
        }

        const fetchFactAdviceSubItems = await fetchDataWithPagination({ customParams })
        const sortedSubItems = sortObjectsAlphabetically(fetchFactAdviceSubItems, "name")

        const subItems = await Promise.all(
          sortedSubItems.map(async subStory => ({
            id: subStory.id,
            name: subStory.name,
            url: subStory.full_slug,
            start_page: subStory.is_startpage,
            parent_id: subStory.parent_id,
            header_icon: subStory.content.header_icon ?? null
          }))
        )

        return {
          id: story.id,
          name: story.name,
          url: story.full_slug,
          start_page: story.is_startpage,
          parent_id: story.parent_id,
          header_icon: story.content.header_icon ?? null,
          children: subItems
        }
      })
    )) as FactAdviceListItem[]

    return {
      stories: fetchFactAdvicePages
    }
  } catch (err) {
    console.error("Failed fetching: ", err)
    throw err
  }
}

const getRegions = async () => {
  const customParams: ISbStoriesParams = {
    ...defaultSbParams,
    starts_with: "data/regioner/"
  }

  const fetchRegions = fetchDataWithPagination({
    customParams
  }).then(result => {
    const regionsData = result.map(region => {
      return {
        id: region.id,
        name: region.name,
        content: region.content
      }
    })
    return regionsData
  }) as Promise<RegionsItem[]>

  const regions = await fetchRegions

  return {
    regions: sortObjectsAlphabetically(regions, "name")
  }
}

const getInitialPressreleases = async () => {
  const slug = "press/"

  const sbParams: ISbStoriesParams = {
    ...defaultSbParams
  }

  const { data: page } = (await getStoryblokApi().get(`cdn/stories/${slug}`, sbParams)) as ISbStory

  const customParams: ISbStoriesParams = {
    ...defaultSbParams,
    starts_with: slug,
    excluding_slugs: slug,
    sort_by: "content.published_date:desc",
    per_page: 4,
    page: 1,
    cv: page.cv
  }

  const pressreleases = (await getStoryblokApi().get(`cdn/stories`, customParams)) as ISbStories

  return {
    stories: pressreleases,
    cacheVersion: page.cv
  }
}

/**
 * Vaccine configs by their numeric `id`, for resolving `relation_id`.
 *
 * `relation_id` is a datasource multi-option, so it holds plain strings like "6" — NOT story
 * references, which means Storyblok's `resolve_relations` cannot expand them. The ids have to be
 * looked up against the configs themselves.
 *
 * Both folders, because a relation may cross them: a travel vaccine and a normal one can name each
 * other, and fetching only the current folder would leave that relation silently rendering nothing.
 *
 * `cache()` for the same reason `fetchStory` has it: two CDN calls per request rather than per
 * caller. Only one call site today, but in draft mode the client's memory cache is flushed on every
 * request, so a second caller would genuinely re-fetch both folders.
 */
const getVaccineConfigsById = cache(async (): Promise<Map<number, VaccineTypeStoryblok>> => {
  const folders = [VACCINES_SLUG, TRAVEL_VACCINES_SLUG]

  const results = await Promise.all(
    folders.map(async folder => {
      const sbParams: ISbStoriesParams = { ...defaultSbParams, starts_with: `${folder}/`, per_page: 100 }
      const { data } = (await getStoryblokApi().get("cdn/stories", sbParams)) as ISbStories
      return data?.stories ?? []
    })
  )

  const byId = new Map<number, VaccineTypeStoryblok>()
  for (const story of results.flat()) {
    const content = story.content as VaccineTypeStoryblok | undefined
    if (content?.component !== "vaccine_type") continue
    // `content.id` — the catalogue number the editor maintains — NOT `story.id`, the Storyblok
    // story id. `relation_id` holds catalogue numbers ("5", "6"), so keying on `story.id` here
    // would make every lookup miss. `Number` because the field comes back as a string from some
    // stories and a number from others.
    const id = Number(content.id)
    // Truthiness, not `!Number.isNaN`: an unset id field reads as "" and `Number("")` is 0, so the
    // NaN check would file every blank-id config under key 0, each overwriting the last. Catalogue
    // numbers start at 1, so nothing real is lost by skipping 0.
    if (id) byId.set(id, content)
  }
  return byId
})

const getServiceConfigsById = cache(async (): Promise<Map<number, ServiceTypeStoryblok>> => {
  const folders = ["vaccination/tjanster"]
  const results = await Promise.all(
    folders.map(async folder => {
      const sbParams: ISbStoriesParams = { ...defaultSbParams, starts_with: `${folder}/`, per_page: 100 }
      const { data } = (await getStoryblokApi().get("cdn/stories", sbParams)) as ISbStories
      return data?.stories ?? []
    })
  )

  const byId = new Map<number, ServiceTypeStoryblok>()
  for (const story of results.flat()) {
    const content = story.content as ServiceTypeStoryblok | undefined
    if (content?.component !== "service_type") continue
    const id = Number(content.id)
    if (id) byId.set(id, content)
  }
  return byId
})

/**
 * Every clinic's öppettider story, keyed by the Webb-ID from the sheet.
 *
 * These stories are machine-owned: the Öppettider sync in dse-web-svc writes them and publishes on
 * every run, which it cannot do to a clinic config an editor might be halfway through. Hours used
 * to live on `vaccination_clinic_type` itself, and that is exactly why a sheet edit could not
 * safely reach the live site.
 *
 * Returned as a plain object rather than a Map: these cross into client components as props.
 */
/** Namespaced so vårdcentraler can get their own set under data/oppettider/ later. */
const OPENING_HOURS_FOLDER = "data/oppettider/vaccinations-mottagningar"

const getOpeningHoursByWebbId = cache(async (): Promise<Record<string, VaccinationClinicOpeningHoursStoryblok>> => {
  const stories = await fetchDataWithPagination<ISbStoryData[]>({
    customParams: {
      ...defaultSbParams,
      content_type: "vaccination_clinic_opening_hours",
      starts_with: `${OPENING_HOURS_FOLDER}/`,
      per_page: 100
    }
  })

  const byWebbId: Record<string, VaccinationClinicOpeningHoursStoryblok> = {}
  for (const story of stories) {
    const content = story.content as VaccinationClinicOpeningHoursStoryblok | undefined
    // The slug IS the Webb-ID, but the field is what the sync matches on, so trust the field and
    // fall back to the slug for a story someone created by hand without filling it in.
    const webbId = String(content?.sheet_webb_id ?? story.slug ?? "").trim()
    if (content && webbId) byWebbId[webbId] = content
  }
  return byWebbId
})

/**
 * Every vaccine's campaign story, keyed by the slug of the folder it lives in — the folder is the
 * relation, the same way `config` is. A vaccine with no campaign story simply has no entry.
 */
const getCampaignStories = cache(
  async () =>
    await fetchDataWithPagination<ISbStoryData[]>({
      customParams: { ...defaultSbParams, content_type: "vaccine_campaign", per_page: 100 }
    })
)

const getCampaignsByVaccineSlug = cache(async (): Promise<Record<string, VaccineCampaignStoryblok>> => {
  const bySlug: Record<string, VaccineCampaignStoryblok> = {}
  for (const story of await getCampaignStories()) {
    const content = story.content as VaccineCampaignStoryblok | undefined
    // "vaccination/vaccin/tbe/campaign" -> "tbe"
    const vaccineSlug = story.full_slug?.split("/").at(-2)
    if (content && vaccineSlug) bySlug[vaccineSlug] = content
  }
  return bySlug
})

/**
 * The same campaigns keyed by the catalogue id instead of the folder slug.
 *
 * Related vaccines are resolved by `relation_id`, so they arrive as bare contents with no slug to
 * look up — the id is the only handle they carry.
 */
const getCampaignsByVaccineId = cache(async (): Promise<Map<number, VaccineCampaignStoryblok>> => {
  const byId = new Map<number, VaccineCampaignStoryblok>()
  for (const story of await getCampaignStories()) {
    const content = story.content as VaccineCampaignStoryblok | undefined
    const id = Number(content?.vaccine_id)
    // Truthiness, not a NaN check: an unset id reads as "" and `Number("")` is 0, which would file
    // every blank-id campaign under the same key. Catalogue numbers start at 1.
    if (content && id) byId.set(id, content)
  }
  return byId
})

/**
 * One clinic's öppettider.
 *
 * Fetched by slug, not picked out of the full list: the story's slug IS the Webb-ID, so a detail
 * page costs one request instead of the four it takes to page through all 108 — and the map above
 * exists for the overview, which genuinely needs every clinic.
 */
const getOpeningHoursForWebbId = async (webbId: string | number | undefined) => {
  const key = String(webbId ?? "").trim()
  if (!key) return undefined

  const { story } = await getStory<VaccinationClinicOpeningHoursStoryblok>(`${OPENING_HOURS_FOLDER}/${key}`)
  return story?.content
}

/**
 * One vaccine's campaign, from the vaccine's own folder — `vaccination/vaccin/tbe/campaign`, the
 * sibling of `config`. Same reasoning: one request rather than listing all 25.
 */
const getCampaignForVaccine = async (folderSlug: string, vaccineSlug: string | undefined) => {
  if (!vaccineSlug) return undefined

  const { story } = await getStory<VaccineCampaignStoryblok>(`${folderSlug}/${vaccineSlug}/campaign`)
  return story?.content
}

/**
 * The configs a vaccine names in `relation_id`, in the order the editor listed them.
 *
 * Unknown ids are dropped rather than rendered as a gap — the relations are hand-maintained for
 * now, so a stale id is expected rather than exceptional.
 */
const getRelatedVaccines = async (vaccine: VaccineTypeStoryblok | undefined): Promise<VaccineTypeStoryblok[]> => {
  const ids = vaccine?.relation_id ?? []
  if (!ids.length) return []

  const [byId, campaignsById] = await Promise.all([getVaccineConfigsById(), getCampaignsByVaccineId()])

  return (
    ids
      .map(id => byId.get(Number(id)))
      .filter((config): config is VaccineTypeStoryblok => Boolean(config))
      // Merged here rather than at the call sites: the detail table renders a related vaccine through
      // the same `isCampaignActive` check as the vaccine itself, so a raw config would quietly report
      // "no campaign" for a vaccine that has one.
      .map(config => mergeVaccineCampaign(config, campaignsById.get(Number(config.id))))
  )
}

const getRelatedServices = async (service: ServiceTypeStoryblok | undefined): Promise<ServiceTypeStoryblok[]> => {
  const ids = service?.relation_id ?? []
  if (!ids.length) return []

  const byId = await getServiceConfigsById()

  return ids.map(id => byId.get(Number(id))).filter((config): config is ServiceTypeStoryblok => Boolean(config))
}

export {
  getArticles,
  getArticleTags,
  getAuthorsById,
  getCampaignForVaccine,
  getCampaignsByVaccineSlug,
  getClinics,
  getFactAdvice,
  getInitialPressreleases,
  getOpeningHoursByWebbId,
  getOpeningHoursForWebbId,
  getRegions,
  getRegionTags,
  getRelatedServices,
  getRelatedVaccines,
  getStory,
  getVaccinationLocations,
  searchFactAdvice
}
